/*  Phase-3 B5 — Employee change-request workflow.
    - Manager submits add/remove/reactivate/update requests.
    - Admin approves or rejects; changes apply atomically on approval.
    - Removal always soft-deactivates so payroll history survives.
    Lives at /employee-requests. Nav-guarded to manager+.
*/
import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { fmtDateDMY } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, UserMinus, RotateCcw, Check, X, Clock } from "lucide-react";

const ACTIONS = [
  { code: "add", label: "Add new employee", icon: UserPlus },
  { code: "remove", label: "Remove (soft-deactivate) employee", icon: UserMinus },
  { code: "reactivate", label: "Reactivate a previously removed employee", icon: RotateCcw },
];

const CATEGORIES = ["Management", "Cashier", "Customer Attendant", "Housekeeper", "Corporate", "Other"];

export default function EmployeeRequests() {
  const { user } = useAuth();
  const isAdmin = ["owner", "admin"].includes(user?.role);
  const [tab, setTab] = useState("pending");   // "pending" | "history" | "new"
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [r, e] = await Promise.all([
        api.get("/phase3/employee_requests", { params: { status: tab === "pending" ? "PENDING" : undefined } }),
        api.get("/payroll/employees"),
      ]);
      setRequests(tab === "history" ? r.data.filter((x) => x.status !== "PENDING") : r.data);
      setEmployees(e.data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="emp-req-root">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Employee change requests</h1>
        <p className="text-sm text-slate-600 mt-1">
          Managers propose changes; only admins approve. Removed employees are soft-deactivated so payroll history stays intact. Every decision is auditable.
        </p>
      </div>

      <div className="flex gap-1 border-2 border-slate-300 rounded-sm bg-white p-0.5 w-fit">
        {[
          { k: "pending", label: "Pending", icon: Clock },
          { k: "history", label: "History", icon: Check },
          { k: "new", label: "New request", icon: UserPlus },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  data-testid={`emp-req-tab-${t.k}`}
                  className={"px-3 py-1.5 text-xs font-bold rounded-sm flex items-center gap-1 " + (tab === t.k ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "new" ? (
        <NewRequestForm employees={employees} onDone={() => { setTab("pending"); load(); }} />
      ) : (
        <RequestList requests={requests} isAdmin={isAdmin} onChanged={load} busy={busy} />
      )}
    </div>
  );
}

function NewRequestForm({ employees, onDone }) {
  const [action, setAction] = useState("add");
  const [reason, setReason] = useState("");
  const [targetCode, setTargetCode] = useState("");
  const [payload, setPayload] = useState({ code: "", name: "", designation: "", category: "Customer Attendant", entity: "VAS" });
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    if (action === "reactivate") return employees.filter((e) => e.active === false);
    if (action === "remove") return employees.filter((e) => e.active !== false);
    return employees;
  }, [employees, action]);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Please add a short reason so the approver knows why."); return; }
    if (action !== "add" && !targetCode) { toast.error("Pick which employee this is for."); return; }
    if (action === "add" && (!payload.name || !payload.code)) { toast.error("Name and Code are required to add someone."); return; }
    setBusy(true);
    try {
      await api.post("/phase3/employee_requests", {
        action, target_code: action === "add" ? null : targetCode,
        payload: action === "add" ? payload : null,
        reason,
      });
      toast.success("Request submitted — waiting on admin approval.");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-4 border-2 border-slate-900 rounded-sm bg-slate-50 space-y-3" data-testid="emp-req-new-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {ACTIONS.map((a) => (
          <button key={a.code} onClick={() => setAction(a.code)} type="button"
                  data-testid={`emp-req-action-${a.code}`}
                  className={"p-3 border-2 rounded-sm text-left flex items-start gap-2 " + (action === a.code ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300 hover:border-slate-400")}>
            <a.icon size={16} className="mt-0.5 shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider">{a.label}</span>
          </button>
        ))}
      </div>

      {action === "add" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <Input placeholder="Code (e.g. 60)" value={payload.code} onChange={(e) => setPayload({ ...payload, code: e.target.value })} data-testid="emp-req-payload-code" />
          <Input placeholder="Full name" value={payload.name} onChange={(e) => setPayload({ ...payload, name: e.target.value })} data-testid="emp-req-payload-name" />
          <Input placeholder="Designation" value={payload.designation} onChange={(e) => setPayload({ ...payload, designation: e.target.value })} data-testid="emp-req-payload-designation" />
          <select value={payload.category} onChange={(e) => setPayload({ ...payload, category: e.target.value })} className="h-10 border border-slate-300 rounded-sm px-2 bg-white" data-testid="emp-req-payload-category">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={payload.entity} onChange={(e) => setPayload({ ...payload, entity: e.target.value })} className="h-10 border border-slate-300 rounded-sm px-2 bg-white">
            <option value="VAS">VAS</option>
            <option value="Corporate">Corporate</option>
          </select>
        </div>
      ) : (
        <select value={targetCode} onChange={(e) => setTargetCode(e.target.value)}
                className="w-full h-10 border border-slate-300 rounded-sm px-2 bg-white"
                data-testid="emp-req-target">
          <option value="">— Pick the employee —</option>
          {filtered.map((e) => (
            <option key={e.code} value={e.code}>{e.name} · {e.code} · {e.category || e.designation}{e.active === false ? " (inactive)" : ""}</option>
          ))}
        </select>
      )}

      <Input placeholder="Reason (mandatory)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="emp-req-reason" />
      <Button onClick={submit} disabled={busy} className="bg-slate-900 text-white h-11" data-testid="emp-req-submit">
        {busy ? "Submitting…" : "Submit request"}
      </Button>
    </Card>
  );
}

function RequestList({ requests, isAdmin, onChanged, busy }) {
  const decide = async (id, decision) => {
    const note = decision === "reject" ? prompt("Rejection reason?") : prompt("Approval note (optional):");
    if (decision === "reject" && !note) return;
    try {
      await api.post(`/phase3/employee_requests/${id}/decide`, { decision, reason: note || "" });
      toast.success(decision === "approve" ? "Approved — change applied." : "Rejected.");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (busy) return <div className="text-xs text-slate-500">Loading…</div>;
  if (!requests.length) return <Card className="p-8 text-center border-2 border-slate-300 border-dashed rounded-sm">Nothing to show here.</Card>;

  return (
    <div className="space-y-2">
      {requests.map((r) => {
        const A = ACTIONS.find((a) => a.code === r.action);
        return (
          <Card key={r.request_id} className="p-3 border-2 border-slate-300 rounded-sm" data-testid={`emp-req-row-${r.request_id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {A && <A.icon size={14} className="text-slate-700" />}
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{A?.label || r.action}</span>
                  <span className={"text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border " + (r.status === "PENDING" ? "state-warn" : r.status === "APPROVED" ? "state-ok" : "state-danger")}>{r.status}</span>
                </div>
                <div className="text-sm mt-1">
                  {r.action === "add" ? <>New: <b>{r.payload?.name}</b> ({r.payload?.code}) · {r.payload?.category || r.payload?.designation}</> :
                    <>Target code <b>{r.target_code}</b>{r.before_snapshot ? <> — {r.before_snapshot.name}</> : null}</>}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Reason: <b>{r.reason || "—"}</b> · Requested by {r.requested_by} on {fmtDateDMY((r.requested_at || "").slice(0, 10))}
                </div>
                {r.decided_by && (
                  <div className="text-xs text-slate-500 mt-1">
                    Decided by {r.decided_by} on {fmtDateDMY((r.decided_at || "").slice(0, 10))}
                    {r.decision_reason ? <> — {r.decision_reason}</> : null}
                    {r.outcome?.note ? <>  · <span className="text-slate-800">{r.outcome.note}</span></> : null}
                  </div>
                )}
              </div>
              {r.status === "PENDING" && isAdmin && (
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" onClick={() => decide(r.request_id, "approve")} className="bg-emerald-700 text-white h-8 text-xs" data-testid={`emp-req-approve-${r.request_id}`}><Check size={12} className="mr-1" />Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => decide(r.request_id, "reject")} className="border-red-400 text-red-700 h-8 text-xs" data-testid={`emp-req-reject-${r.request_id}`}><X size={12} className="mr-1" />Reject</Button>
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
